﻿@file:OptIn(ExperimentalMaterial3Api::class)

package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.getDisplayNameOrHandle
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.AccountsStore
import pl.zar.tikthrow.viewmodel.PlanMode
import pl.zar.tikthrow.viewmodel.PublishingPlan
import pl.zar.tikthrow.viewmodel.QueueViewModel
import pl.zar.tikthrow.viewmodel.WeeklySlot
import pl.zar.tikthrow.viewmodel.dayName
import pl.zar.tikthrow.viewmodel.dayNameFull

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

@Composable
fun PublishingPlanScreen(
    viewModel: QueueViewModel,
    onBack: () -> Unit,
) {
    val plan by viewModel.plan.collectAsState()

    var mode by remember(plan) { mutableStateOf(plan.mode) }
    var isActive by remember(plan) { mutableStateOf(plan.isActive) }
    var weeklySlots by remember(plan) { mutableStateOf(plan.weeklySlots) }
    var postsPerWeek by remember(plan) { mutableStateOf(plan.postsPerWeek) }
    var preferredHour by remember(plan) { mutableStateOf(plan.preferredHour) }
    var preferredMinute by remember(plan) { mutableStateOf(plan.preferredMinute) }
    var selectedPlatforms by remember(plan) { mutableStateOf(plan.platforms) }
    var selectedAccountIds by remember(plan) { mutableStateOf(plan.accountIds) }

    var showTimePicker by remember { mutableStateOf(false) }
    var addingSlotForDay by remember { mutableStateOf<Int?>(null) }
    val timePickerState = rememberTimePickerState(
        initialHour = preferredHour,
        initialMinute = preferredMinute,
        is24Hour = true,
    )

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Column(modifier = Modifier.fillMaxSize()) {
            // Top bar
            Surface(color = Slate100, shadowElevation = 2.dp) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .statusBarsPadding()
                        .padding(horizontal = 8.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Wróć", tint = Slate800)
                    }
                    Text(
                        text = "Plan publikacji",
                        style = TextStyle(brush = gradientBrush, fontSize = 20.sp, fontWeight = FontWeight.ExtraBold)
                    )
                }
            }

            Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                    .widthIn(max = 480.dp)
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                // Active toggle
                Surface(shape = RoundedCornerShape(6.dp), color = Slate100) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Column {
                            Text("Automatyczna publikacja", fontSize = 16.sp, fontWeight = FontWeight.SemiBold, color = Slate900)
                            Text(
                                text = if (isActive) "Plan jest aktywny" else "Plan jest wstrzymany",
                                fontSize = 13.sp,
                                color = if (isActive) GradientStart else Slate500
                            )
                        }
                        Switch(
                            checked = isActive,
                            onCheckedChange = { isActive = it },
                            colors = SwitchDefaults.colors(
                                checkedThumbColor = Color.White,
                                checkedTrackColor = GradientStart,
                                uncheckedThumbColor = Slate400,
                                uncheckedTrackColor = Slate200,
                            )
                        )
                    }
                }

                // Mode selector
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("TRYB PLANU", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        listOf(PlanMode.SLOTS to "Konkretne godziny", PlanMode.FREQUENCY to "Częstotliwość").forEach { (m, label) ->
                            val isSelected = mode == m
                            Surface(
                                onClick = { mode = m },
                                shape = RoundedCornerShape(6.dp),
                                color = if (isSelected) Slate200 else Slate100,
                                border = if (isSelected) androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                                         else androidx.compose.foundation.BorderStroke(1.dp, Slate200),
                                modifier = Modifier.weight(1f)
                            ) {
                                Box(
                                    modifier = Modifier.padding(vertical = 14.dp, horizontal = 8.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = label,
                                        fontSize = 13.sp,
                                        fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                        color = if (isSelected) Slate900 else Slate600,
                                    )
                                }
                            }
                        }
                    }
                }

                val platformColors = mapOf(
                    Platform.TIKTOK    to Color(0xFFFF0050),
                    Platform.INSTAGRAM to Color(0xFFE1306C),
                    Platform.YOUTUBE   to Color(0xFFFF0000),
                    Platform.TWITTER   to Color(0xFF1D9BF0),
                    Platform.FACEBOOK  to Color(0xFF1877F2),
                    Platform.DISCORD   to Color(0xFF5865F2),
                )

                // Platform selector
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("PLATFORMY", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                    val allSelected = selectedPlatforms.isEmpty()
                    Surface(
                        onClick = { selectedPlatforms = emptySet() },
                        shape = RoundedCornerShape(6.dp),
                        color = if (allSelected) Slate200 else Slate100,
                        border = if (allSelected) androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                                 else androidx.compose.foundation.BorderStroke(1.dp, Slate200),
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text("Wszystkie platformy", fontSize = 14.sp,
                                fontWeight = if (allSelected) FontWeight.Bold else FontWeight.Medium,
                                color = if (allSelected) Slate900 else Slate600)
                            Checkbox(
                                checked = allSelected,
                                onCheckedChange = { if (it) selectedPlatforms = emptySet() },
                                colors = CheckboxDefaults.colors(checkedColor = Slate800, uncheckedColor = Slate300, checkmarkColor = Slate50)
                            )
                        }
                    }
                    Platform.entries.forEach { platform ->
                        val isSelected = platform in selectedPlatforms
                        val color = platformColors[platform] ?: Slate600
                        Surface(
                            onClick = {
                                selectedPlatforms = if (isSelected) selectedPlatforms - platform
                                                   else selectedPlatforms + platform
                            },
                            shape = RoundedCornerShape(6.dp),
                            color = if (isSelected) Slate200 else Slate100,
                            border = if (isSelected) androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                                     else androidx.compose.foundation.BorderStroke(1.dp, Slate200),
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                    Box(modifier = Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(color))
                                    Text(platform.displayName, fontSize = 14.sp,
                                        fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                        color = if (isSelected) Slate900 else Slate600)
                                }
                                Checkbox(
                                    checked = isSelected,
                                    onCheckedChange = {
                                        selectedPlatforms = if (isSelected) selectedPlatforms - platform
                                                           else selectedPlatforms + platform
                                    },
                                    colors = CheckboxDefaults.colors(checkedColor = Slate800, uncheckedColor = Slate300, checkmarkColor = Slate50)
                                )
                            }
                        }
                    }
                }

                // Account selector
                val allAccounts by AccountsStore.accounts.collectAsState()
                val visibleAccounts = if (selectedPlatforms.isEmpty()) allAccounts
                                      else allAccounts.filter { it.platform in selectedPlatforms }
                if (visibleAccounts.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Text("KONTA", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                        val allAccountsSelected = selectedAccountIds.isEmpty()
                        Surface(
                            onClick = { selectedAccountIds = emptySet() },
                            shape = RoundedCornerShape(6.dp),
                            color = if (allAccountsSelected) Slate200 else Slate100,
                            border = if (allAccountsSelected) androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                                     else androidx.compose.foundation.BorderStroke(1.dp, Slate200),
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Text("Wszystkie konta", fontSize = 14.sp,
                                    fontWeight = if (allAccountsSelected) FontWeight.Bold else FontWeight.Medium,
                                    color = if (allAccountsSelected) Slate900 else Slate600)
                                Checkbox(
                                    checked = allAccountsSelected,
                                    onCheckedChange = { if (it) selectedAccountIds = emptySet() },
                                    colors = CheckboxDefaults.colors(checkedColor = Slate800, uncheckedColor = Slate300, checkmarkColor = Slate50)
                                )
                            }
                        }
                        visibleAccounts.forEach { account ->
                            val isSelected = account.id in selectedAccountIds
                            val color = platformColors[account.platform] ?: Slate600
                            Surface(
                                onClick = {
                                    selectedAccountIds = if (isSelected) selectedAccountIds - account.id
                                                        else selectedAccountIds + account.id
                                },
                                shape = RoundedCornerShape(6.dp),
                                color = if (isSelected) Slate200 else Slate100,
                                border = if (isSelected) androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                                         else androidx.compose.foundation.BorderStroke(1.dp, Slate200),
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                        Box(modifier = Modifier.size(10.dp).clip(RoundedCornerShape(2.dp)).background(color))
                                        Column {
                                            Text(
                                                text = account.getDisplayNameOrHandle(),
                                                fontSize = 14.sp,
                                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                                color = if (isSelected) Slate900 else Slate900
                                            )
                                            Text(
                                                text = account.platform.displayName,
                                                fontSize = 11.sp,
                                                color = Slate500
                                            )
                                        }
                                    }
                                    Checkbox(
                                        checked = isSelected,
                                        onCheckedChange = {
                                            selectedAccountIds = if (isSelected) selectedAccountIds - account.id
                                                                else selectedAccountIds + account.id
                                        },
                                        colors = CheckboxDefaults.colors(checkedColor = Slate800, uncheckedColor = Slate300, checkmarkColor = Slate50)
                                    )
                                }
                            }
                        }
                    }
                }

                if (mode == PlanMode.SLOTS) {
                    // Slots editor
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Text("GODZINY PUBLIKACJI", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            (1..7).forEach { dow ->
                                val daySlots = weeklySlots.filter { it.dayOfWeek == dow }
                                Surface(shape = RoundedCornerShape(6.dp), color = Slate100) {
                                    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp)) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.SpaceBetween
                                        ) {
                                            Text(
                                                text = dow.dayNameFull,
                                                fontSize = 15.sp,
                                                fontWeight = if (daySlots.isNotEmpty()) FontWeight.SemiBold else FontWeight.Normal,
                                                color = if (daySlots.isNotEmpty()) Slate900 else Slate500
                                            )
                                            Surface(
                                                onClick = {
                                                    addingSlotForDay = dow
                                                    showTimePicker = true
                                                },
                                                shape = CircleShape,
                                                color = Slate200
                                            ) {
                                                Icon(
                                                    Icons.Default.Add,
                                                    contentDescription = "Dodaj godzinę",
                                                    modifier = Modifier.padding(6.dp).size(16.dp),
                                                    tint = Slate600
                                                )
                                            }
                                        }
                                        if (daySlots.isNotEmpty()) {
                                            Spacer(Modifier.height(8.dp))
                                            Row(
                                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                                modifier = Modifier.fillMaxWidth()
                                            ) {
                                                daySlots.sortedBy { it.hour * 60 + it.minute }.forEach { slot ->
                                                    Surface(
                                                        shape = RoundedCornerShape(8.dp),
                                                        color = Slate200
                                                    ) {
                                                        Row(
                                                            modifier = Modifier.padding(start = 10.dp, end = 6.dp, top = 6.dp, bottom = 6.dp),
                                                            verticalAlignment = Alignment.CenterVertically,
                                                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                                                        ) {
                                                            Text(
                                                                "${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}",
                                                                fontSize = 13.sp,
                                                                fontWeight = FontWeight.Bold,
                                                                color = Slate900
                                                            )
                                                            Surface(
                                                                onClick = { weeklySlots = weeklySlots - slot },
                                                                shape = CircleShape,
                                                                color = Color.Transparent
                                                            ) {
                                                                Icon(
                                                                    Icons.Default.Close,
                                                                    contentDescription = "Usuń",
                                                                    modifier = Modifier.size(14.dp),
                                                                    tint = Slate500
                                                                )
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                } else {
                    // Frequency editor
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Text("LICZBA POSTÓW TYGODNIOWO", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                        Surface(shape = RoundedCornerShape(6.dp), color = Slate100) {
                            Column(
                                modifier = Modifier.fillMaxWidth().padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Surface(
                                        onClick = { if (postsPerWeek > 1) postsPerWeek-- },
                                        shape = CircleShape,
                                        color = Slate200,
                                    ) {
                                        Text(
                                            "−",
                                            modifier = Modifier.padding(horizontal = 18.dp, vertical = 10.dp),
                                            fontSize = 20.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = if (postsPerWeek > 1) Slate900 else Slate400
                                        )
                                    }
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(
                                            text = "$postsPerWeek",
                                            style = TextStyle(brush = gradientBrush, fontSize = 36.sp, fontWeight = FontWeight.ExtraBold)
                                        )
                                        Text(
                                            text = if (postsPerWeek == 1) "post tygodniowo" else "posty tygodniowo",
                                            fontSize = 13.sp,
                                            color = Slate500
                                        )
                                    }
                                    Surface(
                                        onClick = { if (postsPerWeek < 7) postsPerWeek++ },
                                        shape = CircleShape,
                                        color = Slate200,
                                    ) {
                                        Text(
                                            "+",
                                            modifier = Modifier.padding(horizontal = 18.dp, vertical = 10.dp),
                                            fontSize = 20.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = if (postsPerWeek < 7) Slate900 else Slate400
                                        )
                                    }
                                }
                                // Day preview chips
                                val previewSlots = viewModel.effectiveSlots(
                                    PublishingPlan(mode = PlanMode.FREQUENCY, postsPerWeek = postsPerWeek, preferredHour = preferredHour, preferredMinute = preferredMinute)
                                )
                                Row(
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    previewSlots.forEach { slot ->
                                        Surface(color = Slate200, shape = RoundedCornerShape(8.dp)) {
                                            Text(
                                                text = slot.dayOfWeek.dayName,
                                                modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                                                fontSize = 12.sp,
                                                fontWeight = FontWeight.SemiBold,
                                                color = Slate600
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Preferred time
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Text("PREFEROWANA GODZINA", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
                        Surface(
                            onClick = {
                                addingSlotForDay = null
                                showTimePicker = true
                            },
                            shape = RoundedCornerShape(6.dp),
                            color = Slate200,
                            border = androidx.compose.foundation.BorderStroke(1.5.dp, Slate600)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Text("Godzina publikacji", fontSize = 15.sp, color = Slate600)
                                Text(
                                    "${preferredHour.toString().padStart(2, '0')}:${preferredMinute.toString().padStart(2, '0')}",
                                    fontSize = 22.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Slate900
                                )
                            }
                        }
                    }
                }

                Spacer(Modifier.height(8.dp))
            }
            }

            // Save button
            Surface(color = Slate100, shadowElevation = 8.dp) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .navigationBarsPadding()
                        .padding(horizontal = 16.dp, vertical = 14.dp)
                        .height(52.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(gradientBrush)
                        .clickable {
                            viewModel.savePlan(
                                PublishingPlan(
                                    mode = mode,
                                    isActive = isActive,
                                    weeklySlots = weeklySlots,
                                    postsPerWeek = postsPerWeek,
                                    preferredHour = preferredHour,
                                    preferredMinute = preferredMinute,
                                    platforms = selectedPlatforms,
                                    accountIds = selectedAccountIds,
                                )
                            )
                            onBack()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Text("Zapisz plan", color = Color.White, fontSize = 15.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = (-0.3).sp)
                }
            }
        }
    }

    if (showTimePicker) {
        AlertDialog(
            onDismissRequest = { showTimePicker = false },
            title = { Text("Wybierz godzinę", fontWeight = FontWeight.Bold, color = Slate900) },
            text = {
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    TimeInput(
                        state = timePickerState,
                        colors = TimePickerDefaults.colors(
                            timeSelectorSelectedContainerColor = GradientStart.copy(0.12f),
                            timeSelectorSelectedContentColor = GradientStart,
                            timeSelectorUnselectedContainerColor = Slate100,
                            timeSelectorUnselectedContentColor = Slate800,
                        )
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    val day = addingSlotForDay
                    if (day != null) {
                        val newSlot = WeeklySlot(day, timePickerState.hour, timePickerState.minute)
                        if (!weeklySlots.any { it.dayOfWeek == day && it.hour == newSlot.hour && it.minute == newSlot.minute }) {
                            weeklySlots = weeklySlots + newSlot
                        }
                    } else {
                        preferredHour = timePickerState.hour
                        preferredMinute = timePickerState.minute
                    }
                    showTimePicker = false
                }) {
                    Text("OK", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                }
            },
            dismissButton = {
                TextButton(onClick = { showTimePicker = false }) {
                    Text("Anuluj", color = Slate500)
                }
            },
            containerColor = Slate100,
            shape = RoundedCornerShape(8.dp)
        )
    }
}
